import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_NavigationContext as NavigationContext, useNavigate } from "react-router-dom";

function useConfirmExit(confirmExit, when = true) {
  const { navigator } = useContext(NavigationContext);

  useEffect(() => {
    if (!when) return;

    const push = navigator.push;
    navigator.push = (...args) => {
      const result = confirmExit(args);
      if (result !== false) {
        push(...args);
      }
    };

    return () => {
      navigator.push = push;
    };
  }, [navigator, confirmExit, when]);
}

export function usePromptOnLeave(message, when = true) {
  const [showModal, setShowModal] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);
  const navigate = useNavigate();
  const isConfirmingRef = useRef(false);

  useEffect(() => {
    if (!when) return;

    window.onbeforeunload = () => message;

    window.history.pushState(null, "", window.location.href);
    window.onpopstate = () => {
      if (isConfirmingRef.current) return;

      setNextLocation("POP");
      setShowModal(true);
      window.history.pushState(null, "", window.location.href);
    };

    return () => {
      window.onbeforeunload = null;
      window.onpopstate = null;
    };
  }, [message, when]);

  const confirmExit = useCallback((args = []) => {
    if (isConfirmingRef.current) return true;

    const [to] = args;
    setNextLocation(to);
    setShowModal(true);
    return false; // Block navigation until confirmed
  }, []);

  const onConfirm = () => {
    isConfirmingRef.current = true;
    setShowModal(false);

    if (nextLocation === "POP") {
      window.onpopstate = null;
      window.history.back();
    } else if (nextLocation) {
      navigate(nextLocation);
    }

    setNextLocation(null);
  };

  const onCancel = () => {
    setShowModal(false);
    setNextLocation(null);
  };

  useConfirmExit(confirmExit, when);

  return { showModal, onConfirm, onCancel };
}
